import React, { useState } from 'react';
// Mise en page menu à gauche / zone à droite, partagée avec la page Configuration
import SettingsLayout from '../components/common/SettingsLayout';
import ProfileForm from '../components/profile/ProfileForm';
import SubscriptionManagement from '../components/profile/SubscriptionManagement';
import NotificationSettings from '../components/notifications/NotificationSettings';
import { CogIcon, KeyIcon, CreditCardIcon, UserCircleIcon, BellIcon, ShieldCheckIcon, LanguageIcon } from '@heroicons/react/24/outline';

/**
 * Ligne de paramètre : libellé à gauche, état courant sous forme de puce et
 * action secondaire à droite. Réutilisable pour tout réglage « valeur + action ».
 */
const SecuritySetting = ({ title, value, onAction, icon, actionText }) => (
  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-4 first:pt-0 last:pb-0">
    <dt className="flex items-center gap-3 text-sm font-medium text-gray-900">
      <span
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500"
        aria-hidden="true"
      >
        {React.cloneElement(icon, { className: 'h-4 w-4' })}
      </span>
      {title}
    </dt>
    <dd className="flex items-center gap-3 text-sm">
      {/* Puce d'état : l'information reste lisible sans dépendre d'une couleur */}
      <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{value}</span>
      <button
        type="button"
        onClick={onAction}
        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition duration-150 hover:bg-gray-50 hover:text-gray-900 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
      >
        {actionText}
      </button>
    </dd>
  </div>
);

/**
 * Sections de la page. Une seule liste pilote le menu et la zone de contenu :
 * ajouter un réglage revient à ajouter une entrée ici.
 */
const SECTIONS = [
  {
    id: 'abonnement',
    title: 'Abonnement',
    description: 'Votre offre, votre consommation et l’historique des paiements.',
    icon: <CreditCardIcon />,
    flush: true,
    content: <SubscriptionManagement />,
  },
  {
    id: 'profil',
    title: 'Profil',
    description: 'Vos informations personnelles et votre mot de passe.',
    icon: <UserCircleIcon />,
    content: <ProfileForm />,
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Choisissez les événements pour lesquels vous souhaitez être alerté.',
    icon: <BellIcon />,
    content: <NotificationSettings />,
  },
  {
    id: 'securite',
    title: 'Sécurité',
    description: 'Authentification et préférences du compte.',
    icon: <KeyIcon />,
    content: (
      <dl className="divide-y divide-gray-200">
        <SecuritySetting
          title="Authentification à deux facteurs"
          value="Désactivée"
          actionText="Activer"
          icon={<ShieldCheckIcon />}
          onAction={() => console.log('Activer 2FA')}
        />
        <SecuritySetting
          title="Langue"
          value="Français"
          actionText="Modifier"
          icon={<LanguageIcon />}
          onAction={() => console.log('Modifier langue')}
        />
      </dl>
    ),
  },
];

const Profile = () => {
  /**
   * Section affichée : initialisée depuis l'ancre de l'URL (ex. /app/profile#abonnement)
   * pour conserver les liens directs de l'ancienne navigation par ancres.
   */
  const [sectionActive, setSectionActive] = useState(() => {
    const ancre = window.location.hash.replace('#', '');
    return SECTIONS.some((section) => section.id === ancre) ? ancre : SECTIONS[0].id;
  });

  return (
    <SettingsLayout
      title="Profil et paramètres"
      description="Gérez vos informations, votre abonnement et vos préférences."
      // Adaptation des sections au format de SettingsLayout (label = titre du menu)
      sections={SECTIONS.map(({ title, content, ...reste }) => ({ ...reste, label: title, content }))}
      activeId={sectionActive}
      onChange={setSectionActive}
    />
  );
};

export default Profile;
